import type {
  Course,
  ApplicationFormData,
  ApplicationFormDataV2,
  ApplicationResponse,
  Application,
  Analytics,
  ApiResponse,
  PaymentStatus,
} from "../types";

/**
 * API Service V2
 * Handles all backend communication
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

// ============ PUBLIC API ============

/**
 * Fetches all available courses
 */
export const fetchCourses = async (): Promise<Course[]> => {
  const response = await fetch(`${API_BASE_URL}/courses`);
  const result: ApiResponse<Course[]> = await response.json();

  if (!result.success || !result.data) {
    throw new Error(result.error || "Failed to fetch courses");
  }

  return result.data;
};

/**
 * Submits a V1 course application (backward compatible)
 */
export const submitApplication = async (
  data: ApplicationFormData
): Promise<ApplicationResponse> => {
  const response = await fetch(`${API_BASE_URL}/applications`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  const result: ApiResponse<ApplicationResponse> = await response.json();

  if (!result.success || !result.data) {
    if (result.details) {
      const errorMessages = result.details
        .map((d) => `${d.field}: ${d.message}`)
        .join(", ");
      throw new Error(errorMessages);
    }
    throw new Error(result.error || "Failed to submit application");
  }

  return result.data;
};

/**
 * Submits a V2 course application with photo upload
 */
export const submitApplicationV2 = async (
  data: ApplicationFormDataV2
): Promise<ApplicationResponse> => {
  const formData = new FormData();

  // Add all fields to FormData
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      if (key === "photo" && value instanceof File) {
        formData.append("photo", value);
      } else {
        formData.append(key, String(value));
      }
    }
  });

  const response = await fetch(`${API_BASE_URL}/applications/v2`, {
    method: "POST",
    body: formData,
  });

  const result: ApiResponse<ApplicationResponse> = await response.json();

  if (!result.success || !result.data) {
    if (result.details) {
      const errorMessages = result.details
        .map((d) => `${d.field}: ${d.message}`)
        .join(", ");
      throw new Error(errorMessages);
    }
    throw new Error(result.error || "Failed to submit application");
  }

  return result.data;
};

/**
 * Mark payment as submitted ("I Have Paid")
 */
export const markPaymentSubmitted = async (applicationId: string): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/applications/${applicationId}/payment-submitted`,
    { method: "POST" }
  );

  const result: ApiResponse<null> = await response.json();

  if (!result.success) {
    throw new Error(result.error || "Failed to update payment status");
  }
};

// ============ ADMIN API ============

// Helper to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem("admin_token");
};

// Helper to create auth headers
const authHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
};

/**
 * Fetches all applications with optional filters
 */
export const fetchApplications = async (filters?: {
  courseId?: string;
  batch?: string;
  paymentStatus?: PaymentStatus;
}): Promise<Application[]> => {
  const params = new URLSearchParams();
  if (filters?.courseId) params.set("courseId", filters.courseId);
  if (filters?.batch) params.set("batch", filters.batch);
  if (filters?.paymentStatus) params.set("paymentStatus", filters.paymentStatus);

  const url = `${API_BASE_URL}/applications/admin${params.toString() ? `?${params}` : ""}`;
  const response = await fetch(url, { headers: authHeaders() });
  const result: ApiResponse<Application[]> = await response.json();

  if (!result.success || !result.data) {
    throw new Error(result.error || "Failed to fetch applications");
  }

  return result.data;
};

/**
 * Fetches a single application by ID
 */
export const fetchApplication = async (id: string): Promise<Application> => {
  const response = await fetch(`${API_BASE_URL}/applications/admin/${id}`, {
    headers: authHeaders(),
  });
  const result: ApiResponse<Application> = await response.json();

  if (!result.success || !result.data) {
    throw new Error(result.error || "Failed to fetch application");
  }

  return result.data;
};

/**
 * Updates application payment status
 */
export const updateApplicationStatus = async (
  id: string,
  data: { paymentStatus: PaymentStatus; batch?: string }
): Promise<Application> => {
  const response = await fetch(`${API_BASE_URL}/applications/admin/${id}/status`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });

  const result: ApiResponse<Application> = await response.json();

  if (!result.success || !result.data) {
    throw new Error(result.error || "Failed to update status");
  }

  return result.data;
};

/**
 * Fetches dashboard analytics
 */
export const fetchAnalytics = async (): Promise<Analytics> => {
  const response = await fetch(`${API_BASE_URL}/applications/admin/analytics`, {
    headers: authHeaders(),
  });
  const result: ApiResponse<Analytics> = await response.json();

  if (!result.success || !result.data) {
    throw new Error(result.error || "Failed to fetch analytics");
  }

  return result.data;
};
